/* eslint-disable */
import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { motion } from "framer-motion";
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Button,
  Chip,
  CircularProgress,
  Typography,
  InputAdornment,
  Stack,
  useTheme,
  MenuItem,
} from "@mui/material";
import {
  Add as AddIcon,
  Search as SearchIcon,
  Event as EventIcon,
  VpnKey as VpnKeyIcon,
} from "@mui/icons-material";
import { fetchPermissions } from "store/features/admin/roleManagement/permission/permissionActions";
import {
  selectPermissions,
  selectPermissionLoading,
} from "store/features/admin/roleManagement/permission/permissionSelectors";
import AddPermissionModal from "./AddPermissionModal";
import permissionsSvg from "assets/folders/permissions.svg";

const Permission = () => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const permissions = useSelector(selectPermissions);
  const loading = useSelector(selectPermissionLoading);

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [resourceFilter, setResourceFilter] = useState("all");

  //for the filters. they will make dropdown from the data fetched from fields of this api get response
  const [actionFilter, setActionFilter] = useState("all");
  const uniqueActions = [...new Set(permissions.map((p) => p.action))].sort();

  useEffect(() => {
    dispatch(fetchPermissions());
  }, [dispatch]);

  // Get unique resources for filter
  const uniqueResources = [
    ...new Set(permissions.map((p) => p.resource)),
  ].sort();

  const filteredPermissions = permissions.filter((permission) => {
    const matchesSearch =
      searchTerm === "" ||
      permission.resource?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      permission.action?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      permission.description?.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesResource =
      resourceFilter === "all" || permission.resource === resourceFilter;

    const matchesAction =
      actionFilter === "all" || permission.action === actionFilter;

    return matchesSearch && matchesResource && matchesAction;
  });

  const formatDateTime = (dateString) => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getActionColor = (action) => {
    switch (action) {
      case "create":
        return "#10b981";
      case "view":
        return "#3b82f6";
      case "update":
        return "#f59e0b";
      case "delete":
        return "#ef4444";
      case "manage":
        return "#8b5cf6";
      case "checkin":
        return "#06b6d4";
      case "checkout":
        return "#0697b0";
      case "apply":
        return "#14b8a6";
      case "approve":
        return "#f97316";
      case "generate":
        return "#ec4899";
      case "send":
        return "#6366f1";
      case "report":
        return "#d2f163";
      default:
        return "#fcb1de";
    }
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        background:
          theme.palette.mode === "dark"
            ? "linear-gradient(135deg, #0a0f1e 0%, #0f172a 50%, #1e1b2e 100%)"
            : "linear-gradient(135deg, #fffbeb 0%, #ffffff 50%, #fefce8 100%)",
        p: 3,
      }}
    >
      {/* Header Section */}
      <Box sx={{ mb: 4 }}>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={2}
          alignItems={{ sm: "center" }}
          justifyContent="space-between"
          sx={{ width: "100%" }}
        >
          <Stack
            direction="row"
            spacing={2}
            alignItems="center"
            sx={{ flex: 1 }}
          >
            <Box
              component="img"
              src={permissionsSvg}
              alt="Permissions"
              sx={{
                width: { xs: 64, lg: 80 },
                height: { xs: 64, lg: 80 },
                objectFit: "contain",
              }}
              onError={(e) => {
                e.target.style.display = "none";
              }}
            />
            <Box>
              <Typography
                variant="h4"
                component="h1"
                sx={{
                  fontWeight: "bold",
                  color: theme.palette.mode === "dark" ? "#ffffff" : "#111827",
                }}
              >
                Permission Management
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: theme.palette.mode === "dark" ? "#9ca3af" : "#6b7280",
                  mt: 0.5,
                }}
              >
                Manage system permissions and access controls
              </Typography>
            </Box>
          </Stack>

          <Box>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setShowCreateModal(true)}
              sx={{
                background: "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)",
                borderRadius: "10px",
                px: 2.5,
                py: 1,
                fontSize: "0.875rem",
                fontWeight: 500,
                textTransform: "none",
                minWidth: "auto",
                whiteSpace: "nowrap",
                boxShadow: "none",
                transition: "all 0.2s ease",
                "&:hover": {
                  background:
                    "linear-gradient(135deg, #d97706 0%, #b45309 100%)",
                  boxShadow: "0 2px 8px rgba(245, 158, 11, 0.3)",
                  transform: "translateY(-1px)",
                },
                "&:active": {
                  transform: "translateY(0)",
                },
              }}
            >
              Add Permission
            </Button>
          </Box>
        </Stack>
      </Box>

      {/* Search and Filter Section */}
      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={2}
        alignItems={{ sm: "center" }}
        sx={{ mb: 3 }}
      >
        <TextField
          placeholder="Search by resource, action or description..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          variant="outlined"
          size="small"
          sx={{
            width: { xs: "100%", sm: 350 },
            "& .MuiOutlinedInput-root": {
              borderRadius: "12px",
              backgroundColor:
                theme.palette.mode === "dark" ? "#1e293b" : "#ffffff",
              "& fieldset": {
                borderColor:
                  theme.palette.mode === "dark" ? "#334155" : "#e5e7eb",
              },
              "&:hover fieldset": {
                borderColor: "#f59e0b",
              },
              "&.Mui-focused fieldset": {
                borderColor: "#f59e0b",
                borderWidth: "2px",
              },
            },
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: "#9ca3af" }} />
              </InputAdornment>
            ),
          }}
        />

        <TextField
          select
          label="Filter by Resource"
          value={resourceFilter}
          onChange={(e) => setResourceFilter(e.target.value)}
          size="small"
          sx={{
            minWidth: { xs: "100%", sm: 180 },
            "& .MuiOutlinedInput-root": {
              borderRadius: "12px",
              backgroundColor:
                theme.palette.mode === "dark" ? "#1e293b" : "#ffffff",
              "& fieldset": {
                borderColor:
                  theme.palette.mode === "dark" ? "#334155" : "#e5e7eb",
              },
              "&:hover fieldset": {
                borderColor: "#f59e0b",
              },
              "&.Mui-focused fieldset": {
                borderColor: "#f59e0b",
                borderWidth: "2px",
              },
            },
            "& .MuiInputLabel-root": {
              color: theme.palette.mode === "dark" ? "#9ca3af" : "#6b7280",
            },
            "& .MuiInputLabel-root.Mui-focused": {
              color: "#f59e0b",
            },
          }}
          SelectProps={{
            MenuProps: {
              PaperProps: {
                sx: {
                  borderRadius: "12px",
                  backgroundColor:
                    theme.palette.mode === "dark" ? "#1e293b" : "#ffffff",
                },
              },
            },
          }}
        >
          <MenuItem value="all">All Resources</MenuItem>
          {uniqueResources.map((resource) => (
            <MenuItem key={resource} value={resource}>
              {resource}
            </MenuItem>
          ))}
        </TextField>

        <TextField
          select
          label="Filter by Action"
          value={actionFilter}
          onChange={(e) => setActionFilter(e.target.value)}
          size="small"
          sx={{
            minWidth: { xs: "100%", sm: 180 },
            "& .MuiOutlinedInput-root": {
              borderRadius: "12px",
              backgroundColor:
                theme.palette.mode === "dark" ? "#1e293b" : "#ffffff",
              "& fieldset": {
                borderColor:
                  theme.palette.mode === "dark" ? "#334155" : "#e5e7eb",
              },
              "&:hover fieldset": {
                borderColor: "#f59e0b",
              },
              "&.Mui-focused fieldset": {
                borderColor: "#f59e0b",
                borderWidth: "2px",
              },
            },
            "& .MuiInputLabel-root": {
              color: theme.palette.mode === "dark" ? "#9ca3af" : "#6b7280",
            },
            "& .MuiInputLabel-root.Mui-focused": {
              color: "#f59e0b",
            },
          }}
          SelectProps={{
            MenuProps: {
              PaperProps: {
                sx: {
                  borderRadius: "12px",
                  backgroundColor:
                    theme.palette.mode === "dark" ? "#1e293b" : "#ffffff",
                },
              },
            },
          }}
        >
          <MenuItem value="all">All Actions</MenuItem>
          {uniqueActions.map((action) => (
            <MenuItem key={action} value={action}>
              {action}
            </MenuItem>
          ))}
        </TextField>

        {(resourceFilter !== "all" || actionFilter !== "all") && (
          <Button
            onClick={() => {
              setResourceFilter("all");
              setActionFilter("all");
            }}
            size="small"
            sx={{
              textTransform: "none",
              borderRadius: "8px",
              px: 2,
              color: "#f59e0b",
              borderColor: "#f59e0b",
              whiteSpace: "nowrap",
              "&:hover": {
                borderColor: "#d97706",
                backgroundColor: "rgba(245,158,11,0.1)",
              },
            }}
            variant="outlined"
          >
            Clear Filters
          </Button>
        )}
      </Stack>

      {/* Loading State */}
      {loading ? (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: 400,
          }}
        >
          <Stack alignItems="center" spacing={2}>
            <CircularProgress
              size={48}
              sx={{
                color: "#f59e0b",
                "& .MuiCircularProgress-circle": {
                  strokeLinecap: "round",
                },
              }}
            />
            <Typography
              sx={{
                color: theme.palette.mode === "dark" ? "#9ca3af" : "#6b7280",
              }}
            >
              Loading permissions...
            </Typography>
          </Stack>
        </Box>
      ) : (
        <Paper
          elevation={0}
          sx={{
            borderRadius: "16px",
            overflow: "hidden",
            boxShadow:
              theme.palette.mode === "dark"
                ? "0 10px 25px -5px rgba(0,0,0,0.3)"
                : "0 10px 25px -5px rgba(0,0,0,0.05), 0 8px 10px -6px rgba(0,0,0,0.02)",
            border: `1px solid ${
              theme.palette.mode === "dark" ? "#1e293b" : "#f3f4f6"
            }`,
          }}
        >
          <TableContainer sx={{ overflowX: "auto" }}>
            <Table sx={{ minWidth: 800 }}>
              <TableHead
                sx={{
                  backgroundColor:
                    theme.palette.mode === "dark" ? "#1e293b" : "#f9fafb",
                }}
              >
                <TableRow>
                  <TableCell
                    sx={{
                      fontWeight: 600,
                      fontSize: "0.75rem",
                      color:
                        theme.palette.mode === "dark" ? "#9ca3af" : "#6b7280",
                    }}
                  >
                    ID
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: 600,
                      fontSize: "0.75rem",
                      color:
                        theme.palette.mode === "dark" ? "#9ca3af" : "#6b7280",
                    }}
                  >
                    Resource
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: 600,
                      fontSize: "0.75rem",
                      color:
                        theme.palette.mode === "dark" ? "#9ca3af" : "#6b7280",
                    }}
                  >
                    Action
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: 600,
                      fontSize: "0.75rem",
                      color:
                        theme.palette.mode === "dark" ? "#9ca3af" : "#6b7280",
                    }}
                  >
                    Description
                  </TableCell>
                  <TableCell
                    align="center"
                    sx={{
                      fontWeight: 600,
                      fontSize: "0.75rem",
                      color:
                        theme.palette.mode === "dark" ? "#9ca3af" : "#6b7280",
                    }}
                  >
                    Created At
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredPermissions.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} align="center" sx={{ py: 6 }}>
                      <VpnKeyIcon
                        sx={{ fontSize: 48, color: "#d1d5db", mb: 1 }}
                      />
                      <Typography
                        sx={{
                          color:
                            theme.palette.mode === "dark"
                              ? "#9ca3af"
                              : "#6b7280",
                        }}
                      >
                        No permissions found. Click "Add Permission" to get
                        started.
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredPermissions.map((permission, index) => (
                    <TableRow
                      key={permission.id}
                      component={motion.tr}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: index * 0.05 }}
                      sx={{
                        "&:hover": {
                          backgroundColor:
                            theme.palette.mode === "dark"
                              ? "rgba(30,41,59,0.5)"
                              : "#f9fafb",
                        },
                      }}
                    >
                      <TableCell>
                        <Typography
                          sx={{
                            fontFamily: "monospace",
                            fontWeight: 600,
                            fontSize: "0.875rem",
                            color: "#f59e0b",
                          }}
                        >
                          #{permission.id}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Stack
                          direction="row"
                          spacing={1.5}
                          alignItems="center"
                        >
                          <Box
                            sx={{
                              width: 36,
                              height: 36,
                              borderRadius: "10px",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                            }}
                          >
                            <VpnKeyIcon
                              sx={{ fontSize: 18, color: "#f59e0b" }}
                            />
                          </Box>
                          <Typography
                            sx={{
                              fontWeight: 600,
                              textTransform: "capitalize",
                              color:
                                theme.palette.mode === "dark"
                                  ? "#ffffff"
                                  : "#111827",
                            }}
                          >
                            {permission.resource}
                          </Typography>
                        </Stack>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={permission.action}
                          size="small"
                          sx={{
                            backgroundColor: `${getActionColor(
                              permission.action,
                            )}20`,
                            color: getActionColor(permission.action),
                            fontWeight: 600,
                            fontSize: "0.7rem",
                            textTransform: "capitalize",
                            border: `1px solid ${getActionColor(
                              permission.action,
                            )}40`,
                          }}
                        />
                      </TableCell>
                      <TableCell sx={{ maxWidth: 300 }}>
                        <Typography
                          sx={{
                            fontSize: "0.75rem",
                            color:
                              theme.palette.mode === "dark"
                                ? "#d1d5db"
                                : "#4b5563",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            display: "-webkit-box",
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: "vertical",
                          }}
                        >
                          {permission.description || "No description"}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Stack
                          direction="row"
                          spacing={1}
                          alignItems="center"
                          justifyContent="center"
                        >
                          <EventIcon sx={{ fontSize: 14, color: "#f59e0b" }} />
                          <Typography
                            sx={{
                              fontSize: "0.75rem",
                              color:
                                theme.palette.mode === "dark"
                                  ? "#d1d5db"
                                  : "#4b5563",
                            }}
                          >
                            {formatDateTime(permission.created_at)}
                          </Typography>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {/* Permission Count Summary */}
      {!loading && permissions.length > 0 && (
        <Stack
          direction="row"
          justifyContent="space-between"
          sx={{
            mt: 2,
            gap: 2,
            fontSize: "0.875rem",
            color: theme.palette.mode === "dark" ? "#9ca3af" : "#6b7280",
          }}
        >
          <Typography variant="body2">
            Total Permissions: {permissions.length}
          </Typography>
          <Typography variant="body2">
            Showing: {filteredPermissions.length} of {permissions.length}
          </Typography>
        </Stack>
      )}

      {/* Empty State for No Search Results */}
      {!loading &&
        filteredPermissions.length === 0 &&
        permissions.length > 0 && (
          <Paper
            sx={{
              mt: 4,
              p: 4,
              textAlign: "center",
              borderRadius: "16px",
              backgroundColor:
                theme.palette.mode === "dark" ? "#1e293b" : "#ffffff",
            }}
          >
            <SearchIcon sx={{ fontSize: 48, color: "#d1d5db", mb: 1 }} />
            <Typography
              sx={{
                color: theme.palette.mode === "dark" ? "#9ca3af" : "#6b7280",
                mb: 2,
              }}
            >
              No permissions match your search criteria
            </Typography>

            <Button
              onClick={() => {
                setSearchTerm("");
                setResourceFilter("all");
                setActionFilter("all");
              }}
              sx={{
                color: "#f59e0b",
                textTransform: "none",
                "&:hover": {
                  backgroundColor:
                    theme.palette.mode === "dark"
                      ? "rgba(245,158,11,0.1)"
                      : "#fffbeb",
                },
              }}
            >
              Clear filters
            </Button>
          </Paper>
        )}

      {/* Add Permission Modal */}
      <AddPermissionModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onSuccess={() => dispatch(fetchPermissions())}
      />
    </Box>
  );
};

export default Permission;
